import React from 'react';
import { Box, keyframes } from '@mui/material';
import { COLORS } from '../../pages/app/reports/groups-teams-activity/constants';

// ─── Pulse animation ───────────────────────────────────
const pulse = keyframes`
    0% { opacity: 0.4; }
    50% { opacity: 1; }
    100% { opacity: 0.4; }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

// ─── Skeleton primitives ───────────────────────────────
const SkeletonBlock: React.FC<{
    width?: string | number;
    height?: string | number;
    borderRadius?: string;
    mb?: string;
}> = ({ width = '100%', height = 16, borderRadius = '6px', mb = '0px' }) => (
    <Box
        sx={{
            width,
            height,
            borderRadius,
            background: `linear-gradient(90deg, ${COLORS.borderLight} 25%, #E8ECF0 50%, ${COLORS.borderLight} 75%)`,
            backgroundSize: '200% 100%',
            animation: `${shimmer} 1.8s ease-in-out infinite`,
            marginBottom: mb,
        }}
    />
);

const SkeletonStatCard: React.FC = () => (
    <Box
        sx={{
            flex: 1,
            backgroundColor: COLORS.cardBg,
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
        }}
    >
        <SkeletonBlock width={40} height={28} borderRadius="4px" />
        <SkeletonBlock width={60} height={12} borderRadius="4px" />
    </Box>
);

const SkeletonChartCard: React.FC = () => (
    <Box
        sx={{
            backgroundColor: COLORS.cardBg,
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            padding: '20px',
            minHeight: 200,
        }}
    >
        <SkeletonBlock width={140} height={14} borderRadius="4px" mb="6px" />
        <SkeletonBlock width={180} height={10} borderRadius="4px" mb="20px" />

        {/* Fake bar chart */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: 120 }}>
            {[65, 45, 80, 55, 70, 40, 90, 50, 60, 75, 85, 55].map((h, i) => (
                <Box
                    key={i}
                    sx={{
                        flex: 1,
                        height: `${h}%`,
                        borderRadius: '3px 3px 0 0',
                        background: `linear-gradient(90deg, ${COLORS.borderLight} 25%, #E8ECF0 50%, ${COLORS.borderLight} 75%)`,
                        backgroundSize: '200% 100%',
                        animation: `${shimmer} 1.8s ease-in-out infinite`,
                        animationDelay: `${i * 0.08}s`,
                    }}
                />
            ))}
        </Box>
    </Box>
);

const SkeletonTableRow: React.FC<{ columns: number; isHeader?: boolean }> = ({ columns, isHeader = false }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: `2fr ${Array(columns - 1).fill('1fr').join(' ')}`,
            gap: '16px',
            padding: '12px 16px',
            borderBottom: `1px solid ${COLORS.borderLight}`,
            alignItems: 'center',
        }}
    >
        {Array.from({ length: columns }).map((_, i) => (
            <SkeletonBlock
                key={i}
                width={i === 0 ? '70%' : '50%'}
                height={isHeader ? 10 : 14}
                borderRadius="4px"
            />
        ))}
    </Box>
);

// ─── Dashboard Skeleton Variants ───────────────────────

interface DashboardSkeletonProps {
    /** Number of stat cards to show */
    statCards?: number;
    /** Number of table columns */
    tableColumns?: number;
    /** Number of table rows */
    tableRows?: number;
    /** Show chart placeholders */
    showCharts?: boolean;
    /** Show filter bar placeholder */
    showFilters?: boolean;
    /** Show progress bar placeholder */
    showProgressBar?: boolean;
}

export const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({
    statCards = 5,
    tableColumns = 6,
    tableRows = 6,
    showCharts = true,
    showFilters = true,
    showProgressBar = false,
}) => (
    <Box sx={{ animation: `${pulse} 2s ease-in-out infinite` }}>
        {/* Breadcrumb skeleton */}
        <Box sx={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
            <SkeletonBlock width={80} height={10} borderRadius="4px" />
            <Box sx={{ color: COLORS.grayMedium, fontSize: '10px' }}>/</Box>
            <SkeletonBlock width={120} height={10} borderRadius="4px" />
        </Box>

        {/* Title skeleton */}
        <Box sx={{ marginBottom: '16px' }}>
            <SkeletonBlock width={220} height={22} borderRadius="4px" mb="6px" />
            <SkeletonBlock width={340} height={11} borderRadius="4px" />
        </Box>

        {/* Filter bar skeleton */}
        {showFilters && (
            <Box sx={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
                {['7d', '30d', '90d', 'Custom'].map((label) => (
                    <Box
                        key={label}
                        sx={{
                            width: 56,
                            height: 30,
                            borderRadius: '6px',
                            background: `linear-gradient(90deg, ${COLORS.borderLight} 25%, #E8ECF0 50%, ${COLORS.borderLight} 75%)`,
                            backgroundSize: '200% 100%',
                            animation: `${shimmer} 1.8s ease-in-out infinite`,
                        }}
                    />
                ))}
                <Box sx={{ flex: 1 }} />
                <SkeletonBlock width={80} height={30} borderRadius="6px" />
                <SkeletonBlock width={80} height={30} borderRadius="6px" />
            </Box>
        )}

        {/* Stat cards skeleton */}
        <Box sx={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            {Array.from({ length: statCards }).map((_, i) => (
                <SkeletonStatCard key={i} />
            ))}
        </Box>

        {/* Progress bar skeleton */}
        {showProgressBar && (
            <Box sx={{ marginBottom: '16px' }}>
                <SkeletonBlock width="100%" height={8} borderRadius="4px" />
            </Box>
        )}

        {/* Charts skeleton */}
        {showCharts && (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '14px',
                    marginBottom: '16px',
                }}
            >
                <SkeletonChartCard />
                <SkeletonChartCard />
            </Box>
        )}

        {/* Table skeleton */}
        <Box
            sx={{
                backgroundColor: COLORS.cardBg,
                borderRadius: '10px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
            }}
        >
            <SkeletonTableRow columns={tableColumns} isHeader />
            {Array.from({ length: tableRows }).map((_, i) => (
                <SkeletonTableRow key={i} columns={tableColumns} />
            ))}
        </Box>
    </Box>
);
